import { Injectable, NgZone } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

/**
 * The app's single socket.io connection.
 *
 * One socket per tab, shared by every feature service: staging runs
 * polling-only (the outer proxy strips the WebSocket upgrade), so a second
 * connection would double the long-poll traffic for nothing.
 *
 * Owns the three things every caller used to get wrong:
 *  - the handshake carries the access token (the server rejects anonymous
 *    connections), and a rejected handshake refreshes the token and retries;
 *  - `onConnect` callbacks re-run on every reconnect, because a reconnect is a
 *    brand-new server session with no room memberships;
 *  - logging out drops the connection, so an authenticated socket never
 *    outlives the session that opened it.
 *
 * Listener registrations survive disconnects: services wire themselves up once
 * and stay wired across a logout/login in the same tab.
 */
@Injectable({ providedIn: 'root' })
export class SocketService {
  private socket?: Socket;
  private readonly listeners = new Map<string, ((payload: unknown) => void)[]>();
  private readonly connectHandlers = new Set<() => void>();
  private refreshing = false;

  constructor(private auth: AuthService, private zone: NgZone) {
    this.auth.currentUser$.subscribe(user => {
      if (!user) { this.dropSocket(); return; }
      // Signed back in on the same tab: hand the new token to the next connect.
      this.updateToken();
    });
  }

  /** True once the handshake completed. */
  get connected(): boolean { return !!this.socket?.connected; }

  /** Opens the connection if it isn't up yet. Safe to call repeatedly. */
  connect(): Socket {
    if (this.socket) return this.socket;

    const socket = io(environment.socketUrl || undefined, {
      path: '/socket.io',
      transports: environment.socketTransports,
      auth: { token: this.auth.accessToken ?? '' },
    });
    this.socket = socket;

    socket.on('connect', () => {
      this.refreshing = false;
      this.zone.run(() => this.connectHandlers.forEach(fn => fn()));
    });

    socket.on('connect_error', (err: Error) => {
      if (err?.message !== 'unauthorized') return;
      // Access token expired while the socket was down. Refresh once, put the
      // new token on the handshake and retry; a failed refresh means the
      // session is really gone and the HTTP interceptor will log the user out.
      if (this.refreshing || !this.auth.refreshToken) return;
      this.refreshing = true;
      this.auth.refresh().subscribe({
        next: () => {
          this.refreshing = false;
          this.updateToken();
          socket.connect();
        },
        error: () => { this.refreshing = false; },
      });
    });

    // Re-attach everything registered before this socket existed.
    this.listeners.forEach((handlers, event) => {
      handlers.forEach(h => socket.on(event, (payload: unknown) => this.zone.run(() => h(payload))));
    });

    return socket;
  }

  /**
   * Registers `handler` for `event`. Handlers survive reconnects, so this is
   * called once per event at service construction — not per room join.
   */
  on<T>(event: string, handler: (payload: T) => void): void {
    const wrapped = handler as (payload: unknown) => void;
    const forEvent = this.listeners.get(event) ?? [];
    forEvent.push(wrapped);
    this.listeners.set(event, forEvent);
    const socket = this.connect();
    socket.on(event, (payload: unknown) => this.zone.run(() => wrapped(payload)));
  }

  /**
   * Runs `fn` on every (re)connect, and immediately if already connected.
   * Use it to re-emit room joins — the server forgets them when the session
   * ends.
   */
  onConnect(fn: () => void): void {
    this.connectHandlers.add(fn);
    this.connect();
    if (this.connected) fn();
  }

  emit(event: string, ...args: unknown[]): void {
    this.connect().emit(event, ...args);
  }

  /** Emits only if the socket is up; for fire-and-forget signals like typing. */
  emitIfConnected(event: string, ...args: unknown[]): void {
    if (this.socket?.connected) this.socket.emit(event, ...args);
  }

  /** Pushes the current access token onto the handshake for the next connect. */
  updateToken(): void {
    if (this.socket) (this.socket as unknown as { auth: Record<string, unknown> }).auth = { token: this.auth.accessToken ?? '' };
  }

  /** Drops the connection (logout). Registrations are kept, so the next
   *  connect() re-authenticates and re-attaches them. */
  disconnect(): void { this.dropSocket(); }

  private dropSocket(): void {
    if (!this.socket) return;
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = undefined;
  }
}
